import { useCallback, useState } from 'react';
import { createApiClient, ApiError as ApiClientError } from '@/lib/adminApiClient';
import type { ApiComponents } from '@/lib/adminApiClient';
import { getStoredAccessToken } from '@/lib/tokenStorage';

// 合规模块所有端点都受 RBAC + 租户隔离保护；typed client 必须通过共享的
// tokenStorage 流向请求附加 `Authorization: Bearer <accessToken>`，
// 否则 /compliance/* 都会拿到 401（Issue #226 sanitized production evidence）。
const apiClient = createApiClient({
  baseUrl: '',
  getAccessToken: () => getStoredAccessToken(),
});

type ProjectComplianceProfileDto = ApiComponents['schemas']['ProjectComplianceProfileDto'];
type UpsertProjectComplianceProfileDto = ApiComponents['schemas']['UpsertProjectComplianceProfileDto'];
type TransitionProjectComplianceStatusDto = ApiComponents['schemas']['TransitionProjectComplianceStatusDto'];

type PolicyFileDto = ApiComponents['schemas']['PolicyFileDto'];
type CreatePolicyFileDto = ApiComponents['schemas']['CreatePolicyFileDto'];
type TransitionPolicyFileStatusDto = ApiComponents['schemas']['TransitionPolicyFileStatusDto'];

type PromotionMaterialDto = ApiComponents['schemas']['PromotionMaterialDto'];
type CreatePromotionMaterialDto = ApiComponents['schemas']['CreatePromotionMaterialDto'];
type TransitionPromotionMaterialDto = ApiComponents['schemas']['TransitionPromotionMaterialDto'];
type PromotionReviewHitDto = ApiComponents['schemas']['PromotionReviewHitDto'];
type PromotionReviewRecordDto = ApiComponents['schemas']['PromotionReviewRecordDto'];
type RecordPromotionReviewDto = ApiComponents['schemas']['RecordPromotionReviewDto'];

type SensitiveTermDto = ApiComponents['schemas']['SensitiveTermDto'];
type CreateSensitiveTermDto = ApiComponents['schemas']['CreateSensitiveTermDto'];
type UpdateSensitiveTermDto = ApiComponents['schemas']['UpdateSensitiveTermDto'];

type ComplianceRecheckTaskDto = ApiComponents['schemas']['ComplianceRecheckTaskDto'];
type AssignRecheckTaskDto = ApiComponents['schemas']['AssignRecheckTaskDto'];
type ResolveRecheckTaskDto = ApiComponents['schemas']['ResolveRecheckTaskDto'];

export interface ApiError {
  message: string;
  code?: string;
}

function toApiError(err: unknown, httpStatus?: number): ApiError {
  // openapi-fetch middleware throws ApiClientError on non-2xx responses, so
  // the catch path supplies the status via the thrown error instead of via
  // the explicit `httpStatus` argument. Fall back to it when the caller did
  // not pass an explicit status (Issue #226).
  const status = httpStatus ?? (err instanceof ApiClientError ? err.status : undefined);
  if (status === 401) {
    return { message: '会话已失效，请重新登录后再试', code: 'UNAUTHENTICATED' };
  }
  if (status === 403) {
    return { message: '当前角色没有合规模块访问权限', code: 'FORBIDDEN' };
  }
  if (status === 409) {
    return { message: '数据已被其他用户修改，请刷新页面后重试', code: 'CONFLICT' };
  }
  if (err instanceof Error) {
    return { message: err.message };
  }
  return { message: String(err) };
}

export function useProjectComplianceProfiles() {
  const [profiles, setProfiles] = useState<ProjectComplianceProfileDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchProfiles = useCallback(
    async (params?: { status?: ProjectComplianceProfileDto['status'] }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/compliance/project-statuses', {
          params: {
            query: params?.status ? { status: params.status } : {},
          },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setProfiles([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const upsertProfile = useCallback(
    async (dto: UpsertProjectComplianceProfileDto): Promise<ProjectComplianceProfileDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/project-statuses', {
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const transitionProfile = useCallback(
    async (id: string, dto: TransitionProjectComplianceStatusDto): Promise<ProjectComplianceProfileDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT('/api/v1/compliance/project-statuses/{id}/status', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { profiles, loading, error, fetchProfiles, upsertProfile, transitionProfile };
}

export function usePolicyFiles() {
  const [policyFiles, setPolicyFiles] = useState<PolicyFileDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPolicyFiles = useCallback(
    async (params?: { status?: PolicyFileDto['status'] }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/compliance/policy-files', {
          params: {
            query: params?.status ? { status: params.status } : {},
          },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setPolicyFiles([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createPolicyFile = useCallback(
    async (dto: CreatePolicyFileDto): Promise<PolicyFileDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/policy-files', {
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const transitionPolicyFile = useCallback(
    async (id: string, dto: TransitionPolicyFileStatusDto): Promise<PolicyFileDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT('/api/v1/compliance/policy-files/{id}/status', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { policyFiles, loading, error, fetchPolicyFiles, createPolicyFile, transitionPolicyFile };
}

export function usePromotionMaterials() {
  const [materials, setMaterials] = useState<PromotionMaterialDto[]>([]);
  const [hits, setHits] = useState<PromotionReviewHitDto[]>([]);
  const [reviews, setReviews] = useState<PromotionReviewRecordDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchMaterials = useCallback(
    async (params?: {
      status?: PromotionMaterialDto['status'];
      materialType?: PromotionMaterialDto['materialType'];
    }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/compliance/promotion-materials', {
          params: {
            query: {
              ...(params?.status ? { status: params.status } : {}),
              ...(params?.materialType ? { materialType: params.materialType } : {}),
            },
          },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setMaterials([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createMaterial = useCallback(
    async (dto: CreatePromotionMaterialDto): Promise<PromotionMaterialDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/promotion-materials', {
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const submitMaterial = useCallback(
    async (id: string, dto: TransitionPromotionMaterialDto): Promise<PromotionMaterialDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/promotion-materials/{id}/submit', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const claimMaterial = useCallback(
    async (id: string, dto: TransitionPromotionMaterialDto): Promise<PromotionMaterialDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/promotion-materials/{id}/claim', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const withdrawMaterial = useCallback(
    async (id: string, dto: TransitionPromotionMaterialDto): Promise<PromotionMaterialDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/promotion-materials/{id}/withdraw', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const reviewMaterial = useCallback(
    async (id: string, dto: RecordPromotionReviewDto): Promise<PromotionReviewRecordDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT('/api/v1/compliance/promotion-materials/{id}/review', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const fetchHits = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET('/api/v1/compliance/promotion-materials/{id}/hits', {
        params: { path: { id } },
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setHits([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchReviews = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: apiErr, response } = await apiClient.GET('/api/v1/compliance/promotion-materials/{id}/reviews', {
        params: { path: { id } },
      });
      if (apiErr) {
        setError(toApiError(apiErr, (response as Response)?.status));
        return;
      }
      setReviews([...(data ?? [])]);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    materials,
    hits,
    reviews,
    loading,
    error,
    fetchMaterials,
    createMaterial,
    submitMaterial,
    claimMaterial,
    withdrawMaterial,
    reviewMaterial,
    fetchHits,
    fetchReviews,
  };
}

export function useSensitiveTerms() {
  const [terms, setTerms] = useState<SensitiveTermDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchTerms = useCallback(
    async (params?: {
      status?: SensitiveTermDto['status'];
      category?: SensitiveTermDto['termCategory'];
    }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/compliance/sensitive-terms', {
          params: {
            query: {
              ...(params?.status ? { status: params.status } : {}),
              ...(params?.category ? { category: params.category } : {}),
            },
          },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setTerms([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createTerm = useCallback(
    async (dto: CreateSensitiveTermDto): Promise<SensitiveTermDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/sensitive-terms', {
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const updateTerm = useCallback(
    async (id: string, dto: UpdateSensitiveTermDto): Promise<SensitiveTermDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.PUT('/api/v1/compliance/sensitive-terms/{id}', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { terms, loading, error, fetchTerms, createTerm, updateTerm };
}

export function useComplianceRecheckTasks() {
  const [tasks, setTasks] = useState<ComplianceRecheckTaskDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchTasks = useCallback(
    async (params?: { status?: ComplianceRecheckTaskDto['status']; assigneeId?: string }) => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.GET('/api/v1/compliance/recheck-tasks', {
          params: {
            query: {
              ...(params?.status ? { status: params.status } : {}),
              ...(params?.assigneeId ? { assigneeId: params.assigneeId } : {}),
            },
          },
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return;
        }
        setTasks([...(data ?? [])]);
      } catch (e) {
        setError(toApiError(e));
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const assignTask = useCallback(
    async (id: string, dto: AssignRecheckTaskDto): Promise<ComplianceRecheckTaskDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/recheck-tasks/{id}/assign', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const resolveTask = useCallback(
    async (id: string, dto: ResolveRecheckTaskDto): Promise<ComplianceRecheckTaskDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/recheck-tasks/{id}/resolve', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const cancelTask = useCallback(
    async (id: string, dto: ResolveRecheckTaskDto): Promise<ComplianceRecheckTaskDto | null> => {
      setLoading(true);
      setError(null);
      try {
        const { data, error: apiErr, response } = await apiClient.POST('/api/v1/compliance/recheck-tasks/{id}/cancel', {
          params: { path: { id } },
          body: dto,
        });
        if (apiErr) {
          setError(toApiError(apiErr, (response as Response)?.status));
          return null;
        }
        return data ?? null;
      } catch (e) {
        setError(toApiError(e));
        return null;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { tasks, loading, error, fetchTasks, assignTask, resolveTask, cancelTask };
}

export type {
  ProjectComplianceProfileDto,
  UpsertProjectComplianceProfileDto,
  TransitionProjectComplianceStatusDto,
  PolicyFileDto,
  CreatePolicyFileDto,
  TransitionPolicyFileStatusDto,
  PromotionMaterialDto,
  CreatePromotionMaterialDto,
  TransitionPromotionMaterialDto,
  PromotionReviewHitDto,
  PromotionReviewRecordDto,
  RecordPromotionReviewDto,
  SensitiveTermDto,
  CreateSensitiveTermDto,
  UpdateSensitiveTermDto,
  ComplianceRecheckTaskDto,
  AssignRecheckTaskDto,
  ResolveRecheckTaskDto,
};
